import React, { useState, useEffect, useMemo } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { setObjectProperty } from 'utils/object'
import STRINGS from 'utils/strings'
import { DATE_FORMAT_MAP, DEFAULT_VISIT_START_HOUR, DEFAULT_VISIT_START_MINUTE } from 'utils/constants'
import { Container, DatePicker, Loader, PromptBar, AutocompleteGroupSearch } from 'components/UIElements'

const visitStartDateField = 'visit_start_datetime'
const startVisitIdField = 'start_visit_id'

const getDateErrors = (date, options) => {
  const inputDateValue = options?.props?.inputProps?.ref?.current?.value
  if (inputDateValue === '') return false
  return !date || typeof date === 'undefined' || moment().isAfter(date, 'day')
}

const VisitScheduleManagement = ({
  clearPtpVisits,
  clearPtpVisitCycles,
  disabled = false,
  fetchParticipantVisits,
  fetchVisitTemplates,
  isCreate,
  participant,
  participantVisits,
  studyID,
  updateField,
  visitTemplatesList,
  visitCyclesList,
  hasVisitsInTemplates,
  hasVisitsInCycles,
  checkedVisitTemplate,
  checkedVisitCycle,
  setCheckedVisitTemplate,
  setCheckedVisitCycle,
  isVisitCycleSupported,
  visitTemplateName,
  setVisitTemplateName,
}) => {
  const [startVisitDateError, setStartVisitDateError] = useState(false)
  const [visitTemplatesLoading, setVisitTemplatesLoading] = useState(false)
  const [isUIUpdated, setisUIUpdated] = useState(null)

  useEffect(() => {
    return () => {
      setisUIUpdated(null)
    }
  }, [])

  const checked = isVisitCycleSupported ? checkedVisitCycle : checkedVisitTemplate
  const setChecked = isVisitCycleSupported ? setCheckedVisitCycle : setCheckedVisitTemplate

  const clearToggle = () => {
    setChecked({})
    updateField(startVisitIdField, '')
    setVisitTemplateName('')
  }

  const onToggleVisit = (item, group) => {
    if (item === null) {
      clearToggle()
    }
    if (
      (item === null &&
        group.value.find(({ key }) => {
          const getItemGroup = group?.field ? checked[group.field] : null
          return getItemGroup && getItemGroup[key]
        })) ||
      (item && checked && checked[item.field] && checked[item.field][item.key])
    ) {
      clearToggle()
    } else if (item) {
      setChecked(setObjectProperty({}, `${item.field}.${item.key}`, true))
      updateField(startVisitIdField, item.value)
      setVisitTemplateName(`${group.label}: ${item.text}`)
    }
  }

  useEffect(() => {
    clearPtpVisits()
    clearPtpVisitCycles()
    if (!isCreate) fetchParticipantVisits(studyID, participant.id)
    setVisitTemplatesLoading(true)
    fetchVisitTemplates(studyID).finally(() => setVisitTemplatesLoading(false))
  }, [])

  const setFixedVisitStartTime = _moment => {
    return _moment.set({ hour: DEFAULT_VISIT_START_HOUR, minute: DEFAULT_VISIT_START_MINUTE })
  }

  if (participant?.visit_start_datetime && isUIUpdated !== true) return null

  if (visitTemplatesLoading) return <Loader inContainer size={25} />

  return (
    <div className='participant-customization'>
      <Container>
        <div className='title'>{STRINGS.visitScheduleManagement}</div>
        <div className='flexed start-justified start-aligned'>
          <span>{STRINGS.ptpNoVisitScheduleAssigned}</span>
        </div>
        <>
          <div>
            <p className='label-small date'>{STRINGS.nextVisitDate}</p>
            <DatePicker
              disabled={disabled || (isVisitCycleSupported ? !hasVisitsInCycles : !hasVisitsInTemplates)}
              hasError={startVisitDateError}
              disabledDays={{ before: new Date() }}
              onDayChange={(date, options) => {
                setStartVisitDateError(getDateErrors(date, options, visitTemplateName))
                if (date) {
                  const dateWithSetTime = setFixedVisitStartTime(date)
                  const formattedDateStr = dateWithSetTime.format(DATE_FORMAT_MAP.datePickerWithFullTime)
                  updateField(visitStartDateField, formattedDateStr)
                  setisUIUpdated(true)
                } else updateField(visitStartDateField, date)
              }}
              date={
                participant?.visit_start_datetime
                  ? moment(participant.visit_start_datetime, DATE_FORMAT_MAP.datePickerWithFullTime)
                  : ''
              }
            />
            <p className='label-small date'>{STRINGS.visitName}</p>
            <AutocompleteGroupSearch
              absolutePositionedList
              list={isVisitCycleSupported ? visitCyclesList : visitTemplatesList || []}
              onSearchEmpty
              filterWithoutGroupSelected
              toggleItem={onToggleVisit}
              checked={checked}
              placeholder='Select...'
              value={visitTemplateName}
              disabled={disabled || (isVisitCycleSupported ? !hasVisitsInCycles : !hasVisitsInTemplates)}
              groupComponent='radio'
              childComponent='checkbox'
              onBackspacePress={clearToggle}
            />
            {(isVisitCycleSupported ? !hasVisitsInCycles : !hasVisitsInTemplates) && (
              <PromptBar>
                <strong>{`${STRINGS.pleaseNote}:`}</strong>
                <span>{STRINGS.ptpNoVisitScheduleCreated}</span>
              </PromptBar>
            )}
          </div>
        </>
      </Container>
    </div>
  )
}

VisitScheduleManagement.propTypes = {
  clearPtpVisits: PropTypes.func,
  disabled: PropTypes.bool,
  visitTemplates: PropTypes.arrayOf(PropTypes.object),
  participantVisits: PropTypes.arrayOf(PropTypes.array),
  fetchParticipantVisits: PropTypes.func,
  updateField: PropTypes.func,
  participant: PropTypes.object,
  studyID: PropTypes.number,
  fetchVisitTemplates: PropTypes.func,
  isCreate: PropTypes.bool,
}

export default VisitScheduleManagement
